import { LoginCredentials, User, AdminUserRegistration, CompleteRegistration } from '../../interfaces/auth';
import api from '../api';
import axios from 'axios';

export const authService = {
  login: async (credentials: LoginCredentials): Promise<{ user: User; token: string }> => {
    try {
      
      // Validar que as credenciais contêm os campos obrigatórios
      if (!credentials.username || !credentials.password) {
        throw new Error('Nome de usuário e senha são obrigatórios');
      }
      
      // Limpar localStorage para evitar problemas com tokens antigos
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      const response = await api.post('/auth/login', credentials);

      const responseData = response.data.data || response.data;
      
      if (!responseData) {
        console.error('Resposta de login inválida:', response.data);
        throw new Error('Formato de resposta inválido');
      }
      
      // Verificar se todos os campos necessários estão presentes
      if (!responseData.id || !responseData.username || !responseData.accessToken) {
        console.error('Dados de resposta incompletos:', responseData);
        throw new Error('Resposta incompleta do servidor');
      }
      
      // Mapeando para o formato esperado pela interface User
      const user: User = {
        id: responseData.id || '',
        username: responseData.username || '',
        email: responseData.email || '',
        firstName: responseData.firstName || '',
        lastName: responseData.lastName || '',
        role: responseData.role || 'Default',
        isObserver: responseData.isObserver || false,
        isRegistrationComplete: responseData.isRegistrationComplete !== false // assume true se não for explicitamente false
      };
      
      // Verificar se o ID do usuário é válido
      if (!user.id) {
        console.error('ID de usuário inválido na resposta:', responseData);
        throw new Error('ID de usuário inválido na resposta');
      }
      
      const token = responseData.accessToken;
      const refreshToken = responseData.refreshToken;
      
      if (!token) {
        console.error('Token não encontrado na resposta:', responseData);
        throw new Error('Token não encontrado na resposta');
      }
      
      
      // Armazena o token e o refreshToken no localStorage
      localStorage.setItem('auth_token', token);
      
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      
      // Armazenar usuário no localStorage
      localStorage.setItem('user', JSON.stringify(user));
      
      // Verificar se as informações foram armazenadas corretamente
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');
      
      if (!storedToken || !storedUser) {
        console.error('Falha ao persistir informações de autenticação no localStorage');
        throw new Error('Falha ao armazenar informações de login. Tente novamente.');
      }
      
      return { user, token };
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      
      // Adicionar detalhes de erro mais específicos
      const err = error as any;
      if (err.response) {
        // Erro da API
        console.error('Detalhes do erro da API:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
        
        if (err.response.status === 401) {
          throw new Error('Nome de usuário ou senha inválidos');
        } else if (err.response.status === 400) {
          const message = err.response.data?.message || err.response.data?.error || 'Dados inválidos';
          throw new Error(`Erro na requisição: ${message}`);
        }
      }
      
      // Limpar localStorage em caso de erro
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    try {
      // Chama a API para fazer logout (invalidar o token no servidor)
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Limpa os dados do localStorage independentemente da resposta do servidor
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },
  
  forgotPassword: async (email: string): Promise<void> => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
    } catch (error) {
      console.error('Erro ao solicitar redefinição de senha:', error);
      throw error;
    }
  },
  
  verifyResetCode: async (email: string, resetCode: string): Promise<boolean> => {
    try {
      const response = await api.post('/auth/verify-reset-code', { email, resetCode });
      return true;
    } catch (error) {
      console.error('Erro ao verificar código de redefinição:', error);
      return false;
    }
  },
  
  resetPassword: async (email: string, resetCode: string, newPassword: string, confirmPassword: string): Promise<boolean> => {
    try {
      const response = await api.post('/auth/reset-password', { 
        email, 
        resetCode, 
        newPassword, 
        confirmPassword 
      });
      return true;
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      throw error;
    }
  },

  getCurrentUser: async (): Promise<User | null> => {
    // Primeiro tenta recuperar do localStorage para não precisar fazer uma chamada à API desnecessária
    const userJson = localStorage.getItem('user');
    
    if (userJson) {
      try {
        return JSON.parse(userJson) as User;
      } catch (error) {
        console.error('Erro ao parser usuário do localStorage:', error);
      }
    }
    
    // Se não encontrou no localStorage ou falhou ao parsear, tenta buscar da API
    try {
      const response = await api.get('/auth/me');
      
      // Extraindo dados da resposta da API
      const responseData = response.data.data || response.data;
      
      // Mapeando para o formato esperado pela interface User
      const user: User = {
        id: responseData.id,
        username: responseData.username,
        email: responseData.email,
        firstName: responseData.firstName,
        lastName: responseData.lastName,
        role: responseData.role,
        isObserver: responseData.isObserver || false,
        isRegistrationComplete: responseData.isRegistrationComplete
      };
      
      // Atualiza o localStorage com os dados mais recentes
      localStorage.setItem('user', JSON.stringify(user));
      
      return user;
    } catch (error) {
      console.error('Erro ao obter usuário atual:', error);
      return null;
    }
  },

  isAuthenticated: (): boolean => {
    // Verifica se há um token no localStorage
    return !!localStorage.getItem('auth_token');
  },
  
  registerUser: async (userData: AdminUserRegistration): Promise<any> => {
    try {
      const response = await api.post('/admin/users/register', userData);
      
      // Extraindo dados da resposta da API
      const responseData = response.data.data || response.data;
      
      // Retornar dados completos com a mensagem
      return {
        user: {
          id: responseData.id,
          username: responseData.username,
          email: responseData.email,
          firstName: responseData.firstName,
          lastName: responseData.lastName,
          role: responseData.role,
          isRegistrationComplete: responseData.isRegistrationComplete || false
        },
        message: response.data.message || 'Usuário registrado com sucesso!'
      };
    } catch (error) {
      console.error('Erro ao registrar usuário:', error);
      throw error;
    }
  },
  
  completeRegistration: async (userData: CompleteRegistration): Promise<{ user: User; token: string }> => {
    try {
      // Obter o token de autorização atual
      const authToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');
      
      if (!authToken) {
        throw new Error('Token de autenticação não encontrado. Faça login novamente.');
      }
      
      if (!storedUser) {
        throw new Error('Dados do usuário não encontrados. Faça login novamente.');
      }
      
      let parsedUser: User;
      try {
        parsedUser = JSON.parse(storedUser) as User;
        if (!parsedUser.id) {
          throw new Error('ID de usuário inválido.');
        }
      } catch (e) {
        throw new Error('Erro ao processar dados do usuário. Faça login novamente.');
      }
      
      // Configuração explícita do Bearer token para garantir que está sendo enviado corretamente
      const config = {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      };
      
      
      // Fazer a requisição com config manual para garantir que o token está indo
      const response = await api.post('/auth/complete-registration', userData, config);
      
      // Extraindo dados da resposta da API
      const responseData = response.data.data || response.data;
      
      if (!responseData) {
        throw new Error('Dados da resposta inválidos');
      }
      
      // Validar campos críticos na resposta
      if (!responseData.id || !responseData.accessToken) {
        throw new Error('Resposta inválida da API - campos obrigatórios ausentes');
      }
      
      // Mapeando para o formato esperado pela interface User
      const user: User = {
        id: responseData.id,
        username: userData.username || responseData.username, // Usar o nome de usuário do formulário se presente
        email: responseData.email || '',
        firstName: userData.firstName, // Usar os valores enviados no formulário
        lastName: userData.lastName,   // Usar os valores enviados no formulário
        role: responseData.role || parsedUser.role || 'Default',
        isObserver: responseData.isObserver || parsedUser.isObserver || false,
        isRegistrationComplete: true // Forçar para true independente da resposta
      };
      
      // Usar nomes de variáveis diferentes para evitar redeclaração
      const accessToken = responseData.accessToken;
      const refreshToken = responseData.refreshToken;
      
      // Armazena o token e o refreshToken no localStorage
      if (accessToken) {
        localStorage.setItem('auth_token', accessToken);
      }
      
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      
      // Armazenar o usuário atualizado no localStorage
      localStorage.setItem('user', JSON.stringify(user));
      
      return { user, token: accessToken || authToken };
    } catch (error) {
      // Verificar se é um erro de resposta da API
      const err = error as any;
      if (err.response) {
        // Criar mensagem de erro mais amigável
        if (err.response.status === 401) {
          throw new Error('Não autorizado - verifique suas credenciais e tente novamente.');
        } else if (err.response.status === 404) {
          throw new Error('Serviço não encontrado. Entre em contato com o suporte.');
        } else if (err.response.status === 400) {
          const errMsg = err.response.data?.message || err.response.data?.error || 'Dados inválidos';
          throw new Error(`Erro na requisição: ${errMsg}`);
        }
      }
      
      throw error;
    }
  },

  refreshToken: async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      return null;
    }
    
    try {
      const response = await api.post('/auth/refresh-token', { refreshToken });
      
      // Extraindo dados da resposta da API
      const responseData = response.data.data || response.data;
      
      const token = responseData.accessToken;
      const newRefreshToken = responseData.refreshToken;
      
      localStorage.setItem('auth_token', token);
      localStorage.setItem('refresh_token', newRefreshToken);
      
      return token;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      // Se falhar em renovar o token, limpa o localStorage e força o login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return null;
    }
  }
};